import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import ScrollToTop from '@/components/shared/ScrollToTop';
import NativeShell from '@/components/shared/NativeShell';
import BiometricLockGate from '@/components/shared/BiometricLockGate';
import BrandIntro from '@/components/shared/BrandIntro';
import { useAuthStore } from '@/store/authStore';
import LoginPage from '@/pages/LoginPage';
import ApplicantDashboard from '@/pages/applicant/ApplicantDashboard';
import RecruiterDashboard from '@/pages/recruiter/RecruiterDashboard';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import About from '@/pages/marketing/About';
import Careers from '@/pages/marketing/Careers';
import Pricing from '@/pages/marketing/Pricing';
import ForApplicants from '@/pages/marketing/ForApplicants';
import ForEmployers from '@/pages/marketing/ForEmployers';
import MeetJay from '@/pages/marketing/MeetJay';
import ContactUsPublic from '@/pages/marketing/ContactUsPublic';
import Legal from '@/pages/marketing/Legal';
import BlogList from '@/pages/marketing/BlogList';
import BlogPost from '@/pages/marketing/BlogPost';
import '@/App.css';
import { Phone } from 'lucide-react';

function PhoneRequiredModal() {
  const { user, updatePhone } = useAuthStore();
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  if (!user?.phone_required) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await updatePhone(phone);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to update phone');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4" data-testid="phone-required-modal">
      <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-xl w-full max-w-md p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center"><Phone className="w-5 h-5 text-emerald-600 dark:text-emerald-400" /></div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Phone Number Required</h2>
            <p className="text-slate-500 dark:text-zinc-500 text-sm">Please add your phone number to continue</p>
          </div>
        </div>
        {error && <div className="bg-red-500/10 text-red-600 dark:text-red-400 px-3 py-2 rounded-lg mb-3 text-sm">{error}</div>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+919876543210" required
            className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 focus:outline-none focus:border-slate-300 dark:focus:border-zinc-600" data-testid="phone-modal-input" />
          <p className="text-slate-500 dark:text-zinc-600 text-xs">International format with country code (E.164)</p>
          <button type="submit" disabled={saving} className="w-full py-2.5 bg-slate-900 dark:bg-white text-white dark:text-black rounded-lg text-sm font-medium disabled:opacity-50" data-testid="phone-modal-submit">
            {saving ? 'Saving...' : 'Save Phone Number'}
          </button>
        </form>
      </div>
    </div>
  );
}

function ProtectedRoute({ children, allowedRoles }) {
  const { isAuthenticated, isLoading, user } = useAuthStore();
  if (isLoading) return <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-[#0a0a0b]"><div className="text-slate-500 dark:text-zinc-400">Loading...</div></div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user?.role)) return <Navigate to="/" replace />;
  return children;
}

function RoleRedirect() {
  const { user, isLoading, isAuthenticated } = useAuthStore();
  if (isLoading) return <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-[#0a0a0b]"><div className="text-slate-500 dark:text-zinc-400">Loading...</div></div>;
  if (!isAuthenticated) return <HomePage />;
  if (user?.role === 'applicant') return <Navigate to="/applicant" replace />;
  if (user?.role === 'recruiter') return <Navigate to="/recruiter" replace />;
  if (user?.role === 'staffing_head' || user?.role === 'staffing_sub') return <Navigate to="/staffing" replace />;
  if (user?.role === 'support_agent') return <Navigate to="/support" replace />;
  if (user?.role === 'admin') return <Navigate to="/admin" replace />;
  return <Navigate to="/login" replace />;
}

import JayFloatingBot from '@/components/shared/JayFloatingBot';
import HomePage from '@/pages/HomePage';
import InterviewRoom from '@/pages/applicant/InterviewRoom';
import AcceptInvite from '@/pages/AcceptInvite';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import VerifyEmail from '@/pages/VerifyEmail';
import Unsubscribe from '@/pages/Unsubscribe';
import GoogleAuthCallback from '@/pages/GoogleAuthCallback';
import StaffingDashboard from '@/pages/staffing/StaffingDashboard';
import StaffingSignup from '@/pages/StaffingSignup';
import SupportDashboard from '@/pages/support/SupportDashboard';
import SupportSignup from '@/pages/SupportSignup';
import { useParams } from 'react-router-dom';

function LegacyContactRedirect() {
  const { user, isLoading, isAuthenticated } = useAuthStore();
  const { ticketId } = useParams();
  if (isLoading) return <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-[#0a0a0b]"><div className="text-slate-500 dark:text-zinc-400">Loading...</div></div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  const role = user?.active_role || user?.role;
  let base = '/applicant/contact';
  if (role === 'recruiter') base = '/recruiter/contact';
  else if (role === 'staffing_head' || role === 'staffing_sub') base = '/staffing/contact';
  else if (role === 'admin') base = '/admin/contact';
  else if (role === 'support_agent') base = '/support';
  const target = ticketId ? `${base}/ticket/${ticketId}` : base;
  return <Navigate to={target} replace />;
}

function App() {
  const loadUser = useAuthStore((s) => s.loadUser);

  useEffect(() => { loadUser(); }, [loadUser]);

  return (
    <HelmetProvider>
      <BrandIntro />
      <BrowserRouter>
        <ScrollToTop />
        <NativeShell />
        <BiometricLockGate />
        <PhoneRequiredModal />
        <JayFloatingBot />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/staffing-signup" element={<StaffingSignup />} />
          <Route path="/support-signup" element={<SupportSignup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/unsubscribe/:token" element={<Unsubscribe />} />
          <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />
          <Route path="/accept-invite/:token" element={<AcceptInvite />} />
          <Route path="/" element={<RoleRedirect />} />
          {/* Marketing pages */}
          <Route path="/about" element={<About />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/for-applicants" element={<ForApplicants />} />
          <Route path="/for-employers" element={<ForEmployers />} />
          <Route path="/meet-jay" element={<MeetJay />} />
          <Route path="/contact-us" element={<ContactUsPublic />} />
          <Route path="/privacy" element={<Legal kind="privacy" />} />
          <Route path="/terms" element={<Legal kind="terms" />} />
          <Route path="/security" element={<Legal kind="security" />} />
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:slug" element={<BlogPost />} />

          <Route path="/contact" element={<ProtectedRoute><LegacyContactRedirect /></ProtectedRoute>} />
          <Route path="/contact/ticket/:ticketId" element={<ProtectedRoute><LegacyContactRedirect /></ProtectedRoute>} />
          <Route path="/applicant/interview/:interviewId" element={<ProtectedRoute allowedRoles={['applicant']}><InterviewRoom /></ProtectedRoute>} />
          <Route path="/applicant/*" element={<ProtectedRoute allowedRoles={['applicant']}><ApplicantDashboard /></ProtectedRoute>} />
          <Route path="/recruiter/*" element={<ProtectedRoute allowedRoles={['recruiter']}><RecruiterDashboard /></ProtectedRoute>} />
          <Route path="/staffing/*" element={<ProtectedRoute allowedRoles={['staffing_head', 'staffing_sub']}><StaffingDashboard /></ProtectedRoute>} />
          <Route path="/support/ticket/:ticketId" element={<ProtectedRoute allowedRoles={['support_agent', 'admin']}><SupportDashboard /></ProtectedRoute>} />
          <Route path="/support/*" element={<ProtectedRoute allowedRoles={['support_agent', 'admin']}><SupportDashboard /></ProtectedRoute>} />
          <Route path="/admin/*" element={<ProtectedRoute allowedRoles={['admin']}><AdminDashboard /></ProtectedRoute>} />
        </Routes>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
